import { useMemo, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormLabel,
} from '@xxtract/ui';
import { formatDate } from '@/lib/formatters';
import { useToast } from '@/components/feedback/Toaster';
import { useBillingDetails, useSubmitBillingChange } from '../hooks/useBillingDetails';
import type { BillingChangeInput, BillingFieldChange, BillingFieldName } from '../billing.api';

interface FormState {
  address: string;
  zipCode: string;
  city: string;
  poReference: string;
}

const EMPTY_FORM: FormState = { address: '', zipCode: '', city: '', poReference: '' };

const FIELD_LABEL_KEYS: Record<BillingFieldName, string> = {
  address: 'account.billing.fields.address',
  zipCode: 'account.billing.fields.zipCode',
  city: 'account.billing.fields.city',
  poReference: 'account.billing.fields.poReference',
};

/**
 * h12-4 (Route B — wijzigingsverzoek). The "current" values ALWAYS come from the source
 * (address/lastUsedPoReference) — never from a pending request (no optimistic UI, no
 * shadow record, h12-4 anti-pattern guard). A pending/processed request is only ever shown
 * as its OWN status badge alongside the source-derived form.
 */
export function BillingDetailsCard() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { data, isLoading, isError, error, refetch } = useBillingDetails();
  const submitChange = useSubmitBillingChange();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [touched, setTouched] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const address = data?.data?.address ?? null;
  const lastUsedPoReference = data?.data?.lastUsedPoReference ?? null;
  const openRequest = data?.data?.openRequest ?? null;
  const noBillingLink = data?.meta?.noBillingLink ?? false;
  const degraded = data?.meta?.degraded ?? false;

  const sourceValues: FormState = {
    address: address?.address ?? '',
    zipCode: address?.zipCode ?? '',
    city: address?.city ?? '',
    poReference: '', // No master PO value (h12-0 contract) — always starts empty.
  };
  const current = touched ? form : sourceValues;

  const changes: BillingFieldChange[] = useMemo(() => {
    const result: BillingFieldChange[] = [];
    (['address', 'zipCode', 'city'] as const).forEach((field) => {
      const value = current[field].trim();
      const from = sourceValues[field] || null;
      if (value !== '' && value !== (from ?? '')) result.push({ field, from, to: value });
    });
    if (current.poReference.trim() !== '') {
      result.push({ field: 'poReference', from: null, to: current.poReference.trim() });
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, address]);

  function handleFieldChange(field: keyof FormState, value: string) {
    setTouched(true);
    setForm((prev) => ({ ...(touched ? prev : sourceValues), [field]: value }));
  }

  // Named handlers (rather than inline `onChange={(e) => handleFieldChange('address', ...)}`)
  // so the field-name identifier lives in a plain variable, not inside a JSX expression.
  const handleAddressChange = (e: ChangeEvent<HTMLInputElement>) => handleFieldChange('address', e.target.value);
  const handleZipCodeChange = (e: ChangeEvent<HTMLInputElement>) => handleFieldChange('zipCode', e.target.value);
  const handleCityChange = (e: ChangeEvent<HTMLInputElement>) => handleFieldChange('city', e.target.value);
  const handlePoReferenceChange = (e: ChangeEvent<HTMLInputElement>) => handleFieldChange('poReference', e.target.value);

  function handleSubmit() {
    if (changes.length === 0) {
      addToast({ key: 'account.billing.noChanges' }, 'info');
      return;
    }
    setConfirmOpen(true);
  }

  function handleConfirm() {
    const input: BillingChangeInput = {};
    for (const change of changes) input[change.field] = change.to;

    submitChange.mutate(input, {
      onSuccess: () => {
        setConfirmOpen(false);
        setTouched(false);
        setForm(EMPTY_FORM);
        addToast({ key: 'account.billing.submitSuccess' });
      },
      onError: () => {
        setConfirmOpen(false);
        addToast({ key: 'errors.generic' }, 'error');
      },
    });
  }

  if (isError) {
    return (
      <Card data-testid="billing-details-card">
        <CardHeader><CardTitle>{t('account.billing.title')}</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-[var(--foreground-muted)]">{String(error)}</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>{t('common.retry')}</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="billing-details-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('account.billing.title')}</CardTitle>
        {openRequest != null && (
          <Badge data-testid="billing-request-status" variant={openRequest.status === 'processed' ? 'success' : 'secondary'} role="status">
            {t(`account.billing.status.${openRequest.status}`, { date: formatDate(openRequest.submittedAt) })}
          </Badge>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isLoading ? (
          <div className="skeleton h-24 rounded-lg" data-testid="billing-details-loading" />
        ) : noBillingLink ? (
          <p data-testid="billing-no-billing-link" className="text-sm text-[var(--foreground-muted)]">{t('account.billing.noBillingLink')}</p>
        ) : degraded ? (
          <div data-testid="billing-details-degraded" className="flex flex-col items-start gap-2">
            <p className="text-sm text-[var(--foreground-muted)]">{t('account.billing.degraded')}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>{t('common.retry')}</Button>
          </div>
        ) : (
          <>
            <Form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
              <FormField id="billing-address">
                <FormLabel>{t('account.billing.fields.address')}</FormLabel>
                <FormControl
                  data-testid="billing-field-address"
                  value={current.address}
                  onChange={handleAddressChange}
                />
              </FormField>
              <div className="grid grid-cols-2 gap-3">
                <FormField id="billing-zip">
                  <FormLabel>{t('account.billing.fields.zipCode')}</FormLabel>
                  <FormControl
                    data-testid="billing-field-zipCode"
                    value={current.zipCode}
                    onChange={handleZipCodeChange}
                  />
                </FormField>
                <FormField id="billing-city">
                  <FormLabel>{t('account.billing.fields.city')}</FormLabel>
                  <FormControl
                    data-testid="billing-field-city"
                    value={current.city}
                    onChange={handleCityChange}
                  />
                </FormField>
              </div>
              <FormField id="billing-po-reference">
                <FormLabel>
                  {t('account.billing.fields.poReference')}{' '}
                  <span className="text-xs font-normal text-[var(--foreground-muted)]">{t('account.billing.fields.poReferenceHint')}</span>
                </FormLabel>
                <FormControl
                  data-testid="billing-field-poReference"
                  placeholder={t('account.billing.fields.poReferenceNotSet')}
                  value={current.poReference}
                  onChange={handlePoReferenceChange}
                />
                {lastUsedPoReference != null && (
                  <p className="text-xs text-[var(--foreground-muted)]">
                    {t('account.billing.fields.poReferenceLastUsed', { reference: lastUsedPoReference })}
                  </p>
                )}
              </FormField>

              <div className="flex items-start gap-2.5 rounded-[10px] border border-[var(--border)] bg-[var(--info-light,var(--background-muted))] p-3 px-4">
                <span className="material-symbols-outlined shrink-0 text-[16px] text-[var(--secondary)]" aria-hidden="true">info</span>
                <p className="text-xs text-[var(--foreground)]">{t('account.billing.infoText')}</p>
              </div>

              <Button type="submit" disabled={changes.length === 0} className="self-start">
                {t('account.billing.submit')}
              </Button>
            </Form>

            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <DialogContent data-testid="billing-confirm-dialog">
                <DialogHeader>
                  <DialogTitle>{t('account.billing.confirmDialog.title')}</DialogTitle>
                  <DialogDescription>{t('account.billing.confirmDialog.description')}</DialogDescription>
                </DialogHeader>
                <ul className="flex flex-col gap-2">
                  {changes.map((change) => (
                    <li key={change.field} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-[var(--foreground-muted)]">{t(FIELD_LABEL_KEYS[change.field])}</span>
                      <span>
                        {change.from ?? t('account.billing.fields.poReferenceNotSet')}
                        {' → '}
                        <strong>{change.to}</strong>
                      </span>
                    </li>
                  ))}
                </ul>
                <DialogFooter>
                  <DialogClose><Button variant="outline">{t('account.billing.confirmDialog.cancel')}</Button></DialogClose>
                  <Button onClick={handleConfirm} disabled={submitChange.isPending}>
                    {t('account.billing.confirmDialog.confirm')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </CardContent>
    </Card>
  );
}
